import { PHI, TWO_PI } from "./local_mods/math_constants";
import {
  frand_rng,
  arr_pick,
  fib_num,
  fx_shuffle_arr,
  lerp,
} from "./local_mods/util";

const gen_search = function (p55, scene, searches, spawn_pt) {
  let loc = { norm: { x: 0.5, y: 0.5 } };
  if (typeof spawn_pt !== "undefined") {
    loc = spawn_pt;
  }

  // normalization factor used for scaling
  const norm_fact = 843; // fib_num(13)

  // normalized search length used for scaling.
  // the length of my var names directly relates to the length of time I've been working on a project ;)
  const search_len_scl_norm = searches.length / scene.num_trees.final;

  const radius_fact = fib_num(
    Math.floor(lerp(7, 4, search_len_scl_norm, scene.i_OPR))
  );

  // Amount to increase radius for each new guess (generation)
  const norm_radius_incr = radius_fact / norm_fact;

  const stroke_w_fact = fib_num(
    Math.floor(lerp(5.76, 0.18, search_len_scl_norm, scene.i_OPR))
  );

  // Used to set strokeWeight
  const norm_stroke_w = stroke_w_fact / norm_fact;

  // Set max/min search space for this new tree
  const curr_sspace = { min: 0 };
  const curr_space_lerp_exp = arr_pick([1, 4, 7, 11]);
  // Next line here is ridiculously convoluted.
  // It sets the max value of the search space for a new tree based on the number of previous searches with a lerp/bias added on.
  curr_sspace.max = fib_num(
    Math.floor(
      lerp(
        2,
        scene.sspace.max_fact,
        search_len_scl_norm,
        Math.pow(scene.i_OPR, curr_space_lerp_exp)
      )
    )
  );

  const curr_rng = {
    min: 0,
    max: curr_sspace.max,
  };

  // Perlin noise offset. Used in generating new guesses and secret_num
  const pn_off = {};
  pn_off.z = searches.length * scene.pn_shift_incr;

  const secret_num = Math.floor(curr_sspace.max * p55.noise(0, 0, pn_off.z));

  let found = { is: false };
  const set_found = function (val) {
    found.is = val;
  };

  const get_found = function () {
    return found.is;
  };

  const gens = [];
  let curr_guess_num;

  const guess_num = function (min, max, idx) {
    // Perlin noise offset increment for each new guess/generation
    const pn_guess_off_incr = 0.011;
    const pn_rad_fact = pn_guess_off_incr * idx;

    pn_off.x = Math.pow(idx / 29, 4); // minimal impact until 11+ gens, large and incr impact after 29 gens
    pn_off.y = pn_rad_fact;
    const pn_guess = p55.noise(pn_off.x, pn_off.y, pn_off.z + 1);

    const guess = min + Math.floor((max - min) * pn_guess);
    return guess;
  };

  const update = function (curr_dims) {
    let found_val = get_found();
    if (found_val === false) {
      // First run through
      if (gens.length < 1) {
        curr_guess_num = guess_num(curr_rng.min, curr_rng.max + 1, 0);
        const curr_gen = new_search_gen(curr_guess_num);
        gens.push(curr_gen);
      }

      // create a new guess
      curr_guess_num = guess_num(curr_rng.min, curr_rng.max, gens.length);
      const curr_gen = new_search_gen(curr_guess_num);
      gens.push(curr_gen);
    } else {
      if (searches.length < scene.num_trees.final) {
        const min_dim = Math.min(curr_dims.x, curr_dims.y); // use this to scale up the norm'd line lengths
        const sn_radius = ((gens.length - 1) * norm_radius_incr * min_dim) / 2;
        let found_pt = {};
        found_pt.actual = {
          x:
            loc.actual.x +
            sn_radius *
              Math.cos(
                scene.init_rot + TWO_PI * (secret_num / curr_sspace.max)
              ),
          y:
            loc.actual.y +
            sn_radius *
              Math.sin(
                scene.init_rot + TWO_PI * (secret_num / curr_sspace.max)
              ),
        };
        found_pt.norm = {
          x: found_pt.actual.x / curr_dims.x,
          y: found_pt.actual.y / curr_dims.y,
        };
        searches.push(gen_search(p55, scene, searches, found_pt));
      }
    }
  };

  const new_search_gen = function (curr_guess) {
    if (curr_guess_num < secret_num) {
      curr_rng.min = curr_guess_num;
    } else if (curr_guess_num > secret_num) {
      curr_rng.max = curr_guess_num;
    } else if (curr_guess_num === secret_num) {
      set_found(true);
      scene.found_count++;
    }

    const gen_rng = { min: curr_rng.min, max: curr_rng.max };

    // Normalized value for how close this generation's guess is to the secret_num
    const guess_delta_norm =
      Math.abs(curr_guess_num - secret_num) / curr_sspace.max;

    const clr_idx = Math.floor(
      lerp(
        scene.pal.clrs.length - 1,
        0,
        guess_delta_norm,
        Math.pow(scene.i_OPR, 4)
      )
    );

    const clr = p55.color(scene.pal.clrs[clr_idx]);

    return {
      curr_guess,
      clr,
      gen_rng,
    };
  };

  const render = function (curr_dims, pg) {
    const min_dim = Math.min(curr_dims.x, curr_dims.y); // use this to scale up the norm'd line lengths
    loc.actual = { x: loc.norm.x * curr_dims.x, y: loc.norm.y * curr_dims.y }; // actual refers to the location in the current render context

    pg.noFill();
    for (let i = gens.length - 1; i > 0; i = i - 1) {
      const g = gens[i];
      const p_g = gens[i - 1];
      const sir = scene.init_rot;
      const p_start_rad = sir + (p_g.gen_rng.min / curr_sspace.max) * TWO_PI;
      const p_end_rad = sir + (p_g.gen_rng.max / curr_sspace.max) * TWO_PI;
      const start_rad = sir + (g.gen_rng.min / curr_sspace.max) * TWO_PI;
      const end_rad = sir + (g.gen_rng.max / curr_sspace.max) * TWO_PI;
      const curr_radius = i * norm_radius_incr * min_dim;
      pg.stroke(p_g.clr);

      pg.strokeWeight(min_dim * norm_stroke_w * Math.pow(scene.i_OPR, 2));
      pg.arc(
        loc.actual.x,
        loc.actual.y,
        curr_radius,
        curr_radius,
        p_start_rad,
        p_end_rad
      );
      pg.stroke(g.clr);
      pg.strokeWeight(min_dim * norm_stroke_w);
      pg.arc(
        loc.actual.x,
        loc.actual.y,
        curr_radius,
        curr_radius,
        start_rad,
        end_rad
      );
    }
  };

  return {
    curr_rng,
    found,
    gens,
    guess_num,
    loc,
    render,
    secret_num,
    update,
  };
};

export { gen_search };
